import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { FileText, Plus, RefreshCcw, CheckCircle, Clock, XCircle, AlertCircle, Calendar, Trash2 } from 'lucide-react';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  useQawaemFilteredRowSelection,
} from '../../products/saudi/components/QawaemTableSelection';
import { QawaemTableStickyActionsTd } from '../../products/saudi/components/QawaemTableStickyActions';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import applyColumnFilters, { textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import { useTranslation } from 'react-i18next';
import { useCrmPage, enrichStatusOptions, resolveCrmModalTabs } from '../../shared/utils/crmPageUtils';
import { CRM_QUOTE_STATUSES } from '../../shared/utils/crmConstants';
import localApiAdapter from '../../core/services/localApiAdapter';
import CustomDatePicker from '../../shared/components/custom/CustomDatePicker';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import QawaemErpCrudModal from '../../products/saudi/components/QawaemErpCrudModal';
import {
  CrmModalFormTabs,
  CrmModalFormBody,
  CrmModalFormPanel,
  CrmModalFieldGrid,
} from './components/CrmModalFormTabs';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import useModuleConfigTheme from '../../shared/hooks/useModuleConfigTheme';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import { CRM_OPPORTUNITIES_REFRESH_EVENT, dispatchCrmApiRefresh } from './crmRefreshEvents';
import { useCrmApiRefreshListener, useCrmClientPartyPicker } from './crmPickerOptions';
import { resolveClientPartySelection } from '../../shared/utils/crmCrossModuleUtils';
import { useCrmConfig } from './useCrmConfig';
import { filterCrmRecordsByDateRange, sliceCrmListItems } from './crmListPageUtils';
import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';

const QUOTE_TABLE_COLUMNS = [
  { key: 'ref', labelKey: 'col_ref' },
  { key: 'client', labelKey: 'col_client' },
  { key: 'value', labelKey: 'col_value' },
  { key: 'status', labelKey: 'col_status', align: 'center' },
  { key: 'valid_until', labelKey: 'col_valid_until' },
  { key: 'created', labelKey: 'col_created' },
];

/** Quotes derived from CRM opportunities at proposal/negotiation stage. */
const QUOTE_STAGES = ['proposal', 'negotiation'];

const emptyForm = () => ({
  opportunity_name: '', contact_id: '', client_name: '', value: '', stage: 'proposal',
  expected_close_date: '', status: 'draft', notes: '',
});

const FORM_ID = 'crm-quotes-form';
const QUOTE_MODAL_FIELD_COUNT = 7;

const QuotesPage = ({ proposalMode = false } = {}) => {
  const { t } = useTranslation();
  const cfgTheme = useModuleConfigTheme();
  const { p, fmtDate, fmtMoney } = useCrmPage(proposalMode ? 'proposalsPage' : 'quotesPage');
  const crmConfig = useCrmConfig();
  const { dateRange } = useSaudiListDateRange();
  const { partyOptions, contacts, clients } = useCrmClientPartyPicker();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const entityLabel = p('entity_label', { defaultValue: p('title') });
  const entityLabelPlural = p('entity_label_plural', { defaultValue: entityLabel });
  const [quotes, setQuotes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [formTab, setFormTab] = useState('quote');
  const [columnVisibility, setColumnVisibility] = useState(() =>
    Object.fromEntries(QUOTE_TABLE_COLUMNS.map((c) => [c.key, true]))
  );

  const qawaemColumnDefs = useMemo(
    () => QUOTE_TABLE_COLUMNS.map((col) => ({
      key: col.key,
      label: p(col.labelKey),
      checked: columnVisibility[col.key] !== false,
    })),
    [columnVisibility, p],
  );
  const handleToggleColumn = (key) => {
    setColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const visibleColumns = useMemo(
    () => QUOTE_TABLE_COLUMNS.filter((c) => columnVisibility[c.key] !== false),
    [columnVisibility],
  );
  const tableColSpan = visibleColumns.length + 2;

  const quoteStatus = (q) => q.quote_status || q.status || (q.stage === 'closed_won' ? 'accepted' : 'sent');

  const getQuoteFilterValue = (q, colKey) => {
    switch (colKey) {
      case 'ref': return q.opportunity_ref || q.opportunity_name || '';
      case 'client': return q.client_name || '';
      case 'value': return fmtMoney(q.value);
      case 'status': return statusOptions.find((s) => s.value === quoteStatus(q))?.label || quoteStatus(q);
      case 'valid_until': return fmtDate(q.expected_close_date);
      case 'created': return fmtDate(q.created_at);
      default: return '';
    }
  };

  const columnFilterRules = useMemo(
    () => visibleColumns.map((col) => {
      const getValue = (row) => getQuoteFilterValue(row, col.key);
      if (col.key === 'status') return exactColumnRule(col.key, getValue);
      return textColumnRule(col.key, getValue);
    }),
    [visibleColumns, statusOptions, fmtDate, fmtMoney],
  );

  const searchFiltered = useMemo(() => quotes.filter((q) =>
    !searchTerm || q.opportunity_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    q.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    q.opportunity_ref?.toLowerCase().includes(searchTerm.toLowerCase())
  ), [quotes, searchTerm]);

  const dateFiltered = useMemo(
    () => filterCrmRecordsByDateRange(searchFiltered, dateRange, ['expected_close_date', 'created_at', 'updated_at']),
    [searchFiltered, dateRange],
  );

  const filtered = useMemo(
    () => applyColumnFilters(dateFiltered, colFilters, columnFilterRules),
    [dateFiltered, colFilters, columnFilterRules],
  );

  const pagedFiltered = useMemo(
    () => sliceCrmListItems(filtered, crmConfig.itemsPerPage),
    [filtered, crmConfig.itemsPerPage],
  );

  const {
    selectedRecords,
    setSelectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord,
  } = useQawaemFilteredRowSelection(filtered);

  useEffect(() => {
    if (selectedRecords.length > 0) setEditingKey(null);
  }, [selectedRecords, setEditingKey]);

  const handleBulkDelete = async () => {
    if (!selectedRecords.length || !window.confirm(p('delete_confirm'))) return;
    try {
      const deleteCount = selectedRecords.length;
      await Promise.all(selectedRecords.map((id) => remove(id)));
      notifyBulkDeleted(showNotify, deleteCount, entityLabelPlural);
      setSelectedRecords([]);
    } catch {
      notifyError(showNotify, p('save_error'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(p('delete_confirm'))) return;
    try {
      await remove(id);
      notifyDeleted(showNotify, entityLabel);
    } catch {
      notifyError(showNotify, p('save_error'));
    }
  };

  const statusFilterOptions = useMemo(
    () => statusOptions.map((s) => ({ value: s.label, label: s.label })),
    [statusOptions],
  );

  const statusOptions = useMemo(() => enrichStatusOptions([
    { value: 'draft', label: CRM_QUOTE_STATUSES.find((s) => s.value === 'draft')?.label || 'مسودة', icon: Clock },
    { value: 'sent', label: CRM_QUOTE_STATUSES.find((s) => s.value === 'sent')?.label || 'مُرسل', icon: AlertCircle },
    { value: 'accepted', label: CRM_QUOTE_STATUSES.find((s) => s.value === 'accepted')?.label || 'مقبول', icon: CheckCircle },
    { value: 'rejected', label: CRM_QUOTE_STATUSES.find((s) => s.value === 'rejected')?.label || 'مرفوض', icon: XCircle },
    { value: 'expired', label: CRM_QUOTE_STATUSES.find((s) => s.value === 'expired')?.label || 'منتهي الصلاحية', icon: XCircle },
  ]), []);

  const handleStatusChange = async (recordId, newStatus) => {
    const quote = quotes.find((q) => q.id === recordId);
    if (!quote) return;
    await save({ ...quote, status: newStatus, quote_status: newStatus }, recordId);
  };

  const load = useCallback(async () => {
    try {
      const response = await localApiAdapter.get('/crm/opportunities');
      const list = Array.isArray(response) ? response : response?.data || [];
      setQuotes(sortRecordsNewestFirst(list.filter((o) => QUOTE_STAGES.includes(o.stage) || o.quote_status)));
    } catch (error) {
      console.error('Error loading quotes:', error);
      setQuotes([]);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  useCrmApiRefreshListener(CRM_OPPORTUNITIES_REFRESH_EVENT, load);

  const stats = useMemo(() => ({
    total: quotes.length,
    draft: quotes.filter((q) => (q.quote_status || q.status) === 'draft').length,
    sent: quotes.filter((q) => (q.quote_status || q.status) === 'sent').length,
    accepted: quotes.filter((q) => (q.quote_status || q.status) === 'accepted' || q.stage === 'closed_won').length,
    totalValue: quotes.reduce((s, q) => s + (parseFloat(q.value) || 0), 0),
  }), [quotes]);

  const save = async (data, id) => {
    const party = resolveClientPartySelection(data.contact_id, { contacts, clients });
    const clientName = party.displayName || data.client_name || '';
    const payload = {
      title: data.opportunity_name || data.title,
      contact_id: party.contactId ? parseInt(party.contactId, 10) : null,
      value: parseFloat(data.value) || 0,
      expected_close_date: data.expected_close_date || null,
      description: data.notes || '',
      stage: 'proposal',
      quote_status: data.status || 'draft',
      client_name: clientName,
    };
    if (id) await localApiAdapter.put(`/crm/opportunities/${id}`, payload);
    else await localApiAdapter.post('/crm/opportunities', payload);
    dispatchCrmApiRefresh('/crm/opportunities');
    await load();
  };

  const remove = async (id) => {
    await localApiAdapter.delete(`/crm/opportunities/${id}`);
    dispatchCrmApiRefresh('/crm/opportunities');
    await load();
  };

  const quoteFormTabs = useMemo(() => resolveCrmModalTabs(QUOTE_MODAL_FIELD_COUNT, [
    { id: 'quote', label: p('tab_quote', { defaultValue: 'العرض' }), icon: FileText },
    { id: 'terms', label: p('tab_terms', { defaultValue: 'الشروط' }), icon: Calendar, iconClassName: 'text-blue-400' },
  ]), [p]);
  const quoteDefaultTab = quoteFormTabs[0]?.id || 'quote';

  const closeModal = () => {
    setFormTab(quoteDefaultTab);
    setShowModal(false);
  };

  const openModal = (record = null) => {
    setEditing(record);
    if (record) {
      setForm({
        ...record,
        contact_id: record.contact_id != null && record.contact_id !== '' ? String(record.contact_id) : '',
        status: quoteStatus(record),
      });
    } else {
      setForm(emptyForm());
    }
    setFormTab(quoteDefaultTab);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await save(form, editing?.id);
      if (editing?.id) {
        notifyUpdated(showNotify, entityLabel);
      } else {
        notifyCreated(showNotify, entityLabel);
      }
      closeModal();
    } catch {
      notifyError(showNotify, p('save_error'));
    }
  };

  const renderQuoteCell = (q, col) => {
    switch (col.key) {
      case 'ref':
        return <td key={col.key} className="px-4 py-3 text-white/80 text-sm">{q.opportunity_ref || q.opportunity_name}</td>;
      case 'client':
        return <td key={col.key} className="px-4 py-3 text-white font-medium">{q.client_name}</td>;
      case 'value':
        return <td key={col.key} className="px-4 py-3 text-white font-bold">{fmtMoney(q.value)}</td>;
      case 'status':
        return (
          <td key={col.key} className="px-4 py-3 text-center">
            <div className="flex justify-center">
              <QawaemClickableStatusBadge
                status={quoteStatus(q)}
                recordId={q.id}
                options={statusOptions}
                onChange={handleStatusChange}
                title={p('click_change_status') || t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' })}
              />
            </div>
          </td>
        );
      case 'valid_until':
        return <td key={col.key} className="px-4 py-3 text-white/70 text-sm">{fmtDate(q.expected_close_date)}</td>;
      case 'created':
        return <td key={col.key} className="px-4 py-3 text-white/60 text-sm">{fmtDate(q.created_at)}</td>;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6 employees-container">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <FileText className="h-8 w-8 text-amber-400" />
            {p('title')}
            <button type="button" onClick={load} className="refresh-button"><RefreshCcw size={20} /></button>
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
        <button type="button" onClick={() => openModal()}
          className="bg-gradient-to-r from-amber-500 to-amber-700 text-white px-6 py-3 rounded-xl font-semibold flex items-center gap-2">
          <Plus size={20} />{p('add')}
        </button>
        {selectedRecords.length > 0 ? (
          <button type="button" onClick={handleBulkDelete} className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-200 text-sm font-medium">
            <Trash2 size={16} /><span>{t('common.delete')} ({selectedRecords.length})</span>
          </button>
        ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { title: p('stat_total'), value: stats.total },
          { title: p('stat_draft'), value: stats.draft },
          { title: p('stat_sent'), value: stats.sent },
          { title: p('stat_accepted'), value: stats.accepted },
          { title: p('stat_total_value'), value: fmtMoney(stats.totalValue) },
        ].slice(0, 4).map((s, i) => (
          <motion.div key={s.title} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            className="bg-white/10 backdrop-blur-lg rounded-2xl p-5 border border-white/20">
            <p className="text-white/60 text-sm">{s.title}</p>
            <p className="text-2xl font-bold text-white mt-1">{s.value}</p>
          </motion.div>
        ))}
      </div>

      <div className="glass-panel glass-table-shell overflow-visible">
        
        <div className="glass-table-scroll overflow-x-auto">
          <table className="w-full employees-table">
            <thead className="glass-table-head">
              <tr>
                <QawaemTableSelectAllHeader
                  allRowsSelected={allRowsSelected}
                  someRowsSelected={someRowsSelected}
                  onChange={handleSelectAll}
                />
                {visibleColumns.map((col) => (
                  <TableColumnHeaderFilter
                    key={col.key}
                    columnKey={col.key}
                    label={p(col.labelKey)}
                    kind={col.key === 'status' ? 'select' : 'text'}
                    value={colFilters[col.key] ?? ''}
                    onChange={(v) => setColFilter(col.key, v)}
                    options={col.key === 'status' ? statusFilterOptions : undefined}
                    editingKey={editingKey}
                    onEditingKeyChange={setEditingKey}
                    align={col.align === 'center' ? 'center' : undefined}
                    thClassName="text-white font-semibold text-sm"
                    className="px-4 py-3"
                  />
                ))}
                <QawaemTableHeaderActionsCell
                  label={p('col_actions')}
                  columns={qawaemColumnDefs}
                  onToggle={handleToggleColumn}
                  visibleCount={qawaemVisibleColumnCount}
                  totalCount={qawaemColumnDefs.length}
                  thClassName="px-4 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                />
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {filtered.length ? pagedFiltered.map((q) => (
                <tr key={q.id} className={`hover:bg-white/5 ${selectedRecords.includes(q.id) ? 'bg-white/10' : ''}`}>
                  <QawaemTableSelectRowCell
                    checked={selectedRecords.includes(q.id)}
                    onChange={() => handleSelectRecord(q.id)}
                  />
                  {visibleColumns.map((col) => renderQuoteCell(q, col))}
                  <QawaemTableStickyActionsTd>
                    <QawaemErpRowActions
                      onEdit={() => openModal(q)}
                      onDelete={() => handleDelete(q.id)}
                      editTitle={t('common.edit')}
                      deleteTitle={t('common.delete')}
                    />
                  </QawaemTableStickyActionsTd>
                </tr>
              )) : (
                <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60">{p('empty')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <QawaemErpCrudModal
          open={showModal}
          onClose={closeModal}
          title={editing ? p('modal_edit') : p('modal_new')}
          icon={FileText}
          formId={FORM_ID}
          saveLabel={t('common.save')}
          onSubmit
          maxWidth="1200px"
          testId="crm-quotes-modal"
        >
          {quoteFormTabs.length > 0 ? (
            <CrmModalFormBody>
              <CrmModalFormTabs
                tabs={quoteFormTabs}
                activeTab={formTab}
                onTabChange={setFormTab}
                ariaLabel={editing ? p('modal_edit') : p('modal_new')}
              />
              <CrmModalFormPanel>
                <form id={FORM_ID} onSubmit={handleSubmit}>
                  {formTab === 'quote' && (
                    <CrmModalFieldGrid>
                      <div>
                        <label className={cfgTheme.label}>{p('field_title', { defaultValue: 'عنوان العرض' })}<span className="text-red-600 font-bold"> *</span></label>
                        <input required value={form.opportunity_name || ''} onChange={(e) => setForm({ ...form, opportunity_name: e.target.value })} className={cfgTheme.input} />
                      </div>
                      <div>
                        <label className={cfgTheme.label}>{p('field_client')}</label>
                        <CustomSelect
                          value={form.contact_id}
                          onChange={(v) => {
                            const party = resolveClientPartySelection(v, { contacts, clients });
                            setForm({ ...form, contact_id: v, client_name: party.displayName });
                          }}
                          options={partyOptions}
                          placeholder={p('select_contact', { defaultValue: 'اختر جهة اتصال' })}
                        />
                      </div>
                      <div>
                        <label className={cfgTheme.label}>{p('field_value')}<span className="text-red-600 font-bold"> *</span></label>
                        <input type="number" step="0.01" required value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className={cfgTheme.input} />
                      </div>
                      <div>
                        <label className={cfgTheme.label}>{p('col_status')}</label>
                        <CustomSelect value={form.status} onChange={(v) => setForm({ ...form, status: v })} options={statusOptions} />
                      </div>
                    </CrmModalFieldGrid>
                  )}
                  {formTab === 'terms' && (
                    <CrmModalFieldGrid>
                      <div>
                        <label className={cfgTheme.label}>{p('field_valid_until')}</label>
                        <CustomDatePicker value={form.expected_close_date} onChange={(v) => setForm({ ...form, expected_close_date: v })} showTime={false} />
                      </div>
                      <div className="md:col-span-3">
                        <label className={cfgTheme.label}>{p('field_notes', { defaultValue: 'ملاحظات' })}</label>
                        <textarea value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={4} className={`${cfgTheme.input} resize-none`} />
                      </div>
                    </CrmModalFieldGrid>
                  )}
                </form>
              </CrmModalFormPanel>
            </CrmModalFormBody>
          ) : (
            <form id={FORM_ID} onSubmit={handleSubmit}>
              <CrmModalFieldGrid>
                <div>
                  <label className={cfgTheme.label}>{p('field_title', { defaultValue: 'عنوان العرض' })}<span className="text-red-600 font-bold"> *</span></label>
                  <input required value={form.opportunity_name || ''} onChange={(e) => setForm({ ...form, opportunity_name: e.target.value })} className={cfgTheme.input} />
                </div>
                <div>
                  <label className={cfgTheme.label}>{p('field_client')}</label>
                  <CustomSelect
                    value={form.contact_id}
                    onChange={(v) => {
                      const party = resolveClientPartySelection(v, { contacts, clients });
                      setForm({ ...form, contact_id: v, client_name: party.displayName });
                    }}
                    options={partyOptions}
                    placeholder={p('select_contact', { defaultValue: 'اختر جهة اتصال' })}
                  />
                </div>
                <div>
                  <label className={cfgTheme.label}>{p('field_value')}<span className="text-red-600 font-bold"> *</span></label>
                  <input type="number" step="0.01" required value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className={cfgTheme.input} />
                </div>
                <div>
                  <label className={cfgTheme.label}>{p('col_status')}</label>
                  <CustomSelect value={form.status} onChange={(v) => setForm({ ...form, status: v })} options={statusOptions} />
                </div>
                <div>
                  <label className={cfgTheme.label}>{p('field_valid_until')}</label>
                  <CustomDatePicker value={form.expected_close_date} onChange={(v) => setForm({ ...form, expected_close_date: v })} showTime={false} />
                </div>
                <div className="md:col-span-3">
                  <label className={cfgTheme.label}>{p('field_notes', { defaultValue: 'ملاحظات' })}</label>
                  <textarea value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={4} className={`${cfgTheme.input} resize-none`} />
                </div>
              </CrmModalFieldGrid>
            </form>
          )}
        </QawaemErpCrudModal>
      )}

      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </div>
  );
};

export default QuotesPage;
